'use es6';

import I18n from 'I18n';
import { useMemo } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import PropTypes from 'prop-types';
import { Set as ImmutableSet } from 'immutable';
import { useSelector } from 'react-redux';
import { TableActionsRow, SortTH } from 'foundations-components/transitional/components/Table';
import { Checkbox } from 'foundations-components/transitional/components/Checkbox';
import { HelpIcon } from 'foundations-components/transitional/components/HelpIcon';
import ReportBulkActions from '../bulkActions/ReportBulkActions';
import { CHECKBOX, UPDATED_AT, LAST_VIEWED, selectVisibleColumns } from '../../lib/selectVisibleColumns';
import { selectIsUnusedFilterActive } from '../../selectors/reportsSelector';
import { getUserTimezoneString } from '../../utils/timeUtils';
import { useSelectedRows } from '../../redux/selectedRows';
import { jsx as _jsx } from "react/jsx-runtime";
const ReportsTableHeader = props => {
  const {
    onSortChange,
    sortColumn,
    sortDirection,
    actionableRows
  } = props;
  const visibleColumns = useSelector(selectVisibleColumns);
  const timezoneOption = useMemo(() => ({
    timezone: getUserTimezoneString()
  }), []);
  const isUnusedFilterActive = useSelector(selectIsUnusedFilterActive);
  const {
    selectedRows,
    selectAll,
    selectedAll,
    selectAllDisabled,
    deselectAll
  } = useSelectedRows({
    actionableRows
  });
  const handleSortChange = title => ({
    target: {
      value: direction
    }
  }) => onSortChange(title, direction);
  const CheckboxHeader = /*#__PURE__*/_jsx("th", {
    children: /*#__PURE__*/_jsx(Checkbox, {
      "aria-label": I18n.text('reports.table.header.checkbox'),
      checked: selectedAll,
      disabled: selectAllDisabled,
      indeterminate: !selectedRows.isEmpty() && !selectedAll //table is partially selected, checkbox contains '-'
      ,
      "data-test-id": "report-bulk-actions-select-all",
      onChange: () => {
        if (selectedRows.isEmpty()) {
          selectAll();
        } else {
          deselectAll();
        }
      }
    })
  }, CHECKBOX);
  const getTitle = (key, labelKey, tooltipLabel) => {
    if (!labelKey) {
      return '';
    }
    const isTitleWithTimezone = key === UPDATED_AT || key === LAST_VIEWED;
    if (!tooltipLabel) {
      return /*#__PURE__*/_jsx(FormattedMessage, Object.assign({
        message: labelKey
      }, isTitleWithTimezone && {
        options: timezoneOption
      }));
    }
    return /*#__PURE__*/_jsx(HelpIcon, {
      title: /*#__PURE__*/_jsx(FormattedMessage, {
        message: tooltipLabel
      }),
      children: /*#__PURE__*/_jsx(FormattedMessage, Object.assign({
        message: labelKey
      }, isTitleWithTimezone && {
        options: timezoneOption
      }))
    });
  };
  const getColumnHeader = ({
    key,
    labelKey,
    isSortable,
    tooltipLabel
  }) => {
    if (key === CHECKBOX) {
      return CheckboxHeader;
    }
    if (isSortable && !isUnusedFilterActive) {
      return /*#__PURE__*/_jsx(SortTH, {
        sort: key === sortColumn ? sortDirection : 'none',
        onSortChange: handleSortChange(key),
        children: getTitle(key, labelKey, tooltipLabel)
      }, key);
    }
    return /*#__PURE__*/_jsx("th", {
      children: getTitle(key, labelKey, tooltipLabel)
    }, key);
  };
  const Actions = /*#__PURE__*/_jsx(ReportBulkActions, {
    actionableRows: actionableRows
  });
  return /*#__PURE__*/_jsx("thead", {
    children: /*#__PURE__*/_jsx(TableActionsRow, {
      open: selectedRows.size > 0,
      openAtColumnIndex: 1,
      actions: Actions,
      children: visibleColumns.map(columnData => getColumnHeader(columnData))
    })
  });
};
ReportsTableHeader.propTypes = {
  sortColumn: PropTypes.string,
  sortDirection: PropTypes.string,
  onSortChange: PropTypes.func.isRequired,
  actionableRows: PropTypes.instanceOf(ImmutableSet).isRequired
};
export default ReportsTableHeader;